import { create } from 'zustand';
import type { CitationData } from '../../components/yoda/components/Citation/types/citation';
import type { DataSource } from '@/shared/types/dataSource.types';

export type RightSidebarContentType = 'citations' | 'dataSources' | 'none';

export interface DataSourceItem extends DataSource {
  selected?: boolean;
  metadata?: Record<string, unknown>;
}

interface RightSidebarState {
  // Generic state
  contentType: RightSidebarContentType;
  isVisible: boolean;

  // Content-specific data
  citations: CitationData[];
  dataSources: DataSourceItem[];

  // Generic actions
  showSidebar: (type: RightSidebarContentType) => void;
  hideSidebar: () => void;

  // Citation-specific actions
  showCitations: (citations: CitationData[]) => void;
  hideCitations: () => void;

  // Data source-specific actions
  showDataSources: (dataSources: DataSourceItem[]) => void;
  hideDataSources: () => void;
  toggleDataSource: (sourceId: string) => void;

  // Utility actions
  clearContent: () => void;
  reset: () => void;
}

export const useRightSidebarStore = create<RightSidebarState>((set, get) => ({
  // Initial state
  contentType: 'none',
  isVisible: false,
  citations: [],
  dataSources: [],

  // Generic actions
  showSidebar: (type: RightSidebarContentType) => {
    set({
      contentType: type,
      isVisible: true,
    });
  },

  hideSidebar: () => {
    set({
      isVisible: false,
      contentType: 'none',
    });
  },

  // Citation-specific actions
  showCitations: (citations: CitationData[]) => {
    set({
      citations,
      contentType: 'citations',
      isVisible: true,
    });
  },

  hideCitations: () => {
    set({
      isVisible: false,
      citations: [],
      contentType: 'none',
    });
  },

  // Data source-specific actions
  showDataSources: (dataSources: DataSourceItem[]) => {
    set({
      dataSources,
      contentType: 'dataSources',
      isVisible: true,
    });
  },

  hideDataSources: () => {
    set({
      isVisible: false,
      dataSources: [],
      contentType: 'none',
    });
  },

  toggleDataSource: (sourceId: string) => {
    const { dataSources } = get();
    set({
      dataSources: dataSources.map(source =>
        source.id === sourceId
          ? { ...source, selected: !source.selected }
          : source
      ),
    });
  },

  // Utility actions
  clearContent: () => {
    set({
      citations: [],
      dataSources: [],
      contentType: 'none',
      isVisible: false,
    });
  },

  reset: () => {
    set({
      contentType: 'none',
      isVisible: false,
      citations: [],
      dataSources: [],
    });
  },
}));

// Backward compatibility: Create a facade that mimics the old citationStore
export const useCitationStoreFacade = () => {
  const { citations, isVisible, contentType, showCitations, hideCitations } =
    useRightSidebarStore();

  return {
    citations,
    isVisible: isVisible && contentType === 'citations',
    showCitations,
    hideCitations,
  };
};
